import { Component, OnInit, ViewChild, inject } from "@angular/core";
import {
  Resource_DetailProjection,
  ResourceModel,
  WorkMode
} from "@app/models/rest/resource";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { ResourceStore } from "@app/services/store/resource-store";
import { MatDialog, MatDialogTitle } from "@angular/material/dialog";
import { ResourceSkillStore } from "@app/services/store/resource-skill-store";
import { ResourceZoneStore } from "@app/services/store/resource-zone-store";
import {
  ChildEntityDeletionBehaviour,
  ChildEntityModel
} from "@app/view/shared/child-entity-manager/child-entity-model";
import { ResourceSkill } from "@app/models/rest/resource-skill";
import {
  ResourceZone,
  ResourceZoneDetailProjection
} from "@app/models/rest/resource-zone";
import { BaseSingleEntityPageComponent } from "@app/view/shared/base-pages/base-single-entity-page/base-single-entity-page.component";
import { ExcludedPropertiesDialogComponent } from "../excluded-properties-dialog/excluded-properties-dialog.component";
import { Store } from "@ngrx/store";
import { ResourceAvailability } from "@app/models/rest/resource-availability";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import { LocationChooserComponent } from "@app/view/shared/location-chooser/location-chooser.component";
import { PostCodeMessage } from "@app/models/dto/postcode-message";
import { LocationStore } from "@app/services/store/location-store";
import { LocationModel } from "@app/models/rest/location";
import { ResourceRule } from "@app/models/rest/resource-rule";
import { ResourceRuleStore } from "@app/services/store/resource-rule-store";
import { ResourceRuleAddComponent } from "@app/view/setup/resourcing/resources/resource-rule-add/resource-rule-add.component";
import { ResourceRuleRenderComponent } from "@app/view/setup/resourcing/resources/resource-rule-render/resource-rule-render.component";
import { Ticket } from "@app/models/rest/ticket";
import { ImageCropperDialogComponent } from "../image-cropper-dialog/image-cropper-dialog.component";
import { ConfirmEfficiencyChangesDialogComponent } from "./confirm-efficiency-changes-dialog.component";
import {
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { TeamStore } from "@app/services/store/team-store";
import { Team } from "@app/models/rest/team";
import { Observable } from "rxjs";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconAnchor, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import {
  MatCard,
  MatCardHeader,
  MatCardActions,
  MatCardContent,
  MatCardAvatar
} from "@angular/material/card";
import { MatTooltip } from "@angular/material/tooltip";
import {
  MatFormField,
  MatLabel,
  MatError,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker
} from "@angular/material/datepicker";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { ResourceRouteMapComponent } from "../../../../shared/resource-route-map/resource-route-map.component";
import { ResourceSkillMappingComponent } from "../resource-skill-mapping/resource-skill-mapping.component";
import { ResourceZoneMappingComponent } from "../resource-zone-mapping/resource-zone-mapping.component";
import { ResourceRosterAssignmentsComponent } from "../roster-assignments/roster-assignments.component";
import { AsyncPipe } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-resource-detail",
  templateUrl: "./resource-detail.component.html",
  styleUrls: ["./resource-detail.component.scss"],
  imports: [
    MatToolbar,
    MatIconAnchor,
    RouterLink,
    MatIcon,
    MatDialogTitle,
    MatButton,
    MatProgressBar,
    MatCard,
    MatCardHeader,
    MatCardActions,
    MatCardContent,
    MatCardAvatar,
    MatTooltip,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatSuffix,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatCheckbox,
    ResourceRouteMapComponent,
    ResourceSkillMappingComponent,
    ResourceZoneMappingComponent,
    ResourceRosterAssignmentsComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class ResourceDetailComponent
  extends BaseSingleEntityPageComponent<ResourceModel>
  implements OnInit
{
  resourceStore = inject(ResourceStore);
  resourceSkillStore = inject(ResourceSkillStore);
  resourceRuleStore = inject(ResourceRuleStore);
  resourceZoneStore = inject(ResourceZoneStore);
  locationStore = inject(LocationStore);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  teamStore = inject(TeamStore);

  resource: ResourceModel;
  resourceDetail: Resource_DetailProjection;

  public childEntities: {
    skill: ChildEntityModel<ResourceSkill, ResourceModel>;
    zone: ChildEntityModel<ResourceZone, ResourceModel>;
    availability: ChildEntityModel<ResourceAvailability, ResourceModel>;
    resourceRule: ChildEntityModel<ResourceRule, ResourceModel>;
  } = { skill: null, zone: null, availability: null, resourceRule: null };

  public isFullyLoaded: boolean;
  public currentDate: Date = new Date();

  // Snapshot of the loaded efficiency settings, so we can tell a real change
  // from an edit-then-revert (a control's dirty flag stays true after a
  // revert). (#1512)
  private efficiencyBaseline: {
    percentage: number;
    effectiveFrom: string | null;
    effectiveTo: string | null;
  } | null = null;

  // Form controls integrated from resource-property component
  resourceForm = new UntypedFormGroup({
    ResourceName: new UntypedFormControl(),
    ResourceImageURL: new UntypedFormControl(),
    ResourceCost: new UntypedFormControl(),
    IsOfflineWorker: new UntypedFormControl(false),
    Team: new UntypedFormControl(),
    EfficiencyPercentage: new UntypedFormControl(100, [
      Validators.required,
      Validators.min(50),
      Validators.max(150)
    ]),
    EfficiencyEffectiveFrom: new UntypedFormControl(null),
    EfficiencyEffectiveTo: new UntypedFormControl(null)
  });

  // Observable for teams
  teams$: Observable<Team[]>;

  constructor() {
    super();

    this.childEntities.resourceRule = {
      type: ResourceRule,
      relation: "resourceRule",
      display: "ruleValue",
      title: "scheduler.resource-detail.resource-rules.title",
      addComponent: ResourceRuleAddComponent,
      addWidth: "950px",
      displayComponent: ResourceRuleRenderComponent,
      searchKey: "resource",
      readonly: false,
      store: this.resourceRuleStore,
      searchQuery: "findByResource",
      deletionBehaviour: ChildEntityDeletionBehaviour.LINK_TABLE,
      parentStore: null,
      parentType: null,
      parentRelation: null
    };
  }

  ngOnInit() {
    console.log("ResourceDetailComponent ngOnInit starting");

    // Load teams first
    this.teamStore.loadAll(null).then(() => {
      // Teams loaded successfully
    });

    // Get teams observable
    this.teams$ = this.teamStore.entities;

    super.loadDataSource(this.resourceStore).then((entity) => {
      console.log("Resource entity loaded in detail component:", entity);
      console.log("Resource entity team:", entity.team);
      console.log("Resource entity workMode:", entity.workMode);

      this.resourceStore.entity.subscribe((resource) => {
        console.log("Resource updated in entity subscription:", resource);

        this.resourceStore
          .loadSingleProjection<Resource_DetailProjection>(
            Resource_DetailProjection,
            resource.id,
            "detail"
          )
          .then((value) => {
            console.log("Resource detail projection loaded:", value);
            console.log("Resource detail team:", value.team);
            console.log("Resource detail workMode:", value.workMode);

            this.resource = entity;
            this.resourceDetail = value;
            this.isFullyLoaded = true;

            // Load resource rules (excluded properties) for this resource
            this.loadResourceRules();

            // Initialize form values
            this.resourceForm.get("ResourceName").setValue(this.resource.name);
            this.resourceForm
              .get("ResourceImageURL")
              .setValue(this.resource.imageUrl);
            this.resourceForm.get("ResourceCost").setValue(this.resource.cost);

            // Set the IsOfflineWorker checkbox based on the workMode
            // Use resourceDetail for workMode if available, similar to how team is handled
            const isOffline =
              this.resourceDetail && this.resourceDetail.workMode
                ? this.resourceDetail.workMode === WorkMode.Offline
                : this.resource.workMode === WorkMode.Offline;
            this.resourceForm.get("IsOfflineWorker").setValue(isOffline);

            // Efficiency settings: default to 100% (no adjustment) on legacy rows.
            const efficiencySource =
              this.resourceDetail &&
              typeof this.resourceDetail.efficiencyPercentage === "number"
                ? this.resourceDetail
                : this.resource;
            this.resourceForm
              .get("EfficiencyPercentage")
              .setValue(efficiencySource.efficiencyPercentage ?? 100);
            this.resourceForm
              .get("EfficiencyEffectiveFrom")
              .setValue(efficiencySource.efficiencyEffectiveFrom ?? null);
            this.resourceForm
              .get("EfficiencyEffectiveTo")
              .setValue(efficiencySource.efficiencyEffectiveTo ?? null);
            this.efficiencyBaseline = {
              percentage: efficiencySource.efficiencyPercentage ?? 100,
              effectiveFrom: efficiencySource.efficiencyEffectiveFrom ?? null,
              effectiveTo: efficiencySource.efficiencyEffectiveTo ?? null
            };

            // Use resourceDetail for team if available, since it contains the
            // correct team information from the projection
            if (this.resourceDetail && this.resourceDetail.team) {
              this.resourceForm
                .get("Team")
                .setValue(this.resourceDetail.team.id);
            } else if (this.resource.team) {
              this.resourceForm.get("Team").setValue(this.resource.team.id);
            }

            console.log("Resource and resourceDetail set, isFullyLoaded=true");
          });
      });
    });
  }

  retryLoad() {
    super.retry(this.resourceStore);
  }

  selectLocation() {
    this.dialog
      .open(LocationChooserComponent, {})
      .afterClosed()
      .subscribe((value: { result: PostCodeMessage }) => {
        if (!value || !value.result) return;

        console.log("location changed");
        let location: LocationModel = new LocationModel();
        location.latitude = value.result.latitude;
        location.longitude = value.result.longitude;
        console.log("creating location", location);

        this.locationStore
          .findByLongitudeAndLatitude(location.latitude, location.longitude)
          .then((existing) => {
            if (existing.resources.length > 0) {
              console.log("assigning existing", existing);
              this.resource
                .bindRelation("location", existing.resources[0])
                .subscribe(() => {
                  console.log("done");
                  this.resourceStore
                    .loadSingleProjection<Resource_DetailProjection>(
                      Resource_DetailProjection,
                      this.resource.id,
                      "detail"
                    )
                    .then((value) => {
                      this.resourceDetail = value;
                    });
                });
            } else {
              this.locationStore.create(location).subscribe((value1) => {
                this.resource
                  .bindRelation("location", value1 as LocationModel)
                  .subscribe(() => {
                    console.log("done");
                    this.resourceStore
                      .loadSingleProjection<Resource_DetailProjection>(
                        Resource_DetailProjection,
                        this.resource.id,
                        "detail"
                      )
                      .then((value) => {
                        this.resourceDetail = value;
                      });
                  });
              });
            }
          });
      });
  }

  // Team selection is now handled by the resource-property component

  // Handle ticket selection from the map component
  onTicketSelected(ticket: Ticket) {
    console.log("Ticket selected:", ticket);
    // Implement any additional handling here
  }

  // Called when zones are updated in resource-zone-mapping
  filterZones() {
    // This method used to update the map with the selected zones
    // Since we've removed the map functionality, this is now a no-op
    console.log("Zones updated");
  }

  // Returns whether the form has unsaved changes
  isFormDirty(): boolean {
    return this.resourceForm?.dirty || false;
  }

  // Extract initials from a name (e.g., "John Doe" -> "JD")
  getInitials(name: string): string {
    if (!name) return "";

    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  }

  // Open dialog to select an image file, crop it to a circle, and then save
  openImageSelector() {
    // Create a file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";

    // Handle the file selection
    fileInput.addEventListener("change", (event: any) => {
      const file = event.target.files[0];
      if (file) {
        // Open the image cropper dialog
        this.dialog
          .open(ImageCropperDialogComponent, {
            width: "550px",
            data: { imageFile: file }
          })
          .afterClosed()
          .subscribe((result) => {
            if (result) {
              // Result is the base64 of the cropped image
              if (this.resource) {
                this.resource.imageUrl = result;
                // Update the form control value
                this.resourceForm.get("ResourceImageURL").setValue(result);
                this.resourceForm.get("ResourceImageURL").markAsDirty();
              }
            }
          });
      }
    });

    // Trigger the file input click
    fileInput.click();
  }

  // Clear the image and set to default
  clearImage(event: MouseEvent) {
    // Stop event propagation to prevent openImageSelector from being called
    event.stopPropagation();

    if (this.resource) {
      // Clear the image URL
      this.resource.imageUrl = null;

      // Update the form control value
      this.resourceForm.get("ResourceImageURL").setValue(null);
      this.resourceForm.get("ResourceImageURL").markAsDirty();
    }
  }

  saveResource() {
    console.log("====== SAVE RESOURCE WORKFLOW START ======");
    console.log("saveResource called in ResourceDetailComponent");

    if (this.resource) {
      // Log detailed state information
      console.log("Current resource details:");
      console.log("- ID:", this.resource.id);
      console.log("- Name:", this.resource.name);
      console.log("- Current resource workMode:", this.resource.workMode);
      console.log(
        "- Current resource workMode (typeof):",
        typeof this.resource.workMode
      );

      console.log("Form state:");
      console.log(
        "- Form offline checkbox value:",
        this.resourceForm.get("IsOfflineWorker").value
      );
      console.log(
        "- Form offline checkbox value (typeof):",
        typeof this.resourceForm.get("IsOfflineWorker").value
      );
      console.log("- Form team value:", this.resourceForm.get("Team").value);
      console.log("- Form dirty state:", this.resourceForm.dirty);
      console.log("- Complete form values:", this.resourceForm.value);

      // Log dirty state of all controls
      console.log("Dirty state of individual controls:");
      Object.keys(this.resourceForm.controls).forEach((key) => {
        console.log(`- ${key} dirty:`, this.resourceForm.get(key).dirty);
      });

      // Check if the form is dirty before saving
      if (this.resourceForm.dirty) {
        console.log("Form is dirty, calling updateResource");
        // Use update method which has the form handling logic
        this.confirmThenUpdateResource();
      } else {
        console.log("No changes to save - form is not dirty");

        // Check if the IsOfflineWorker control changed but the form isn't marked dirty
        if (
          (this.resource.workMode === "Online" &&
            this.resourceForm.get("IsOfflineWorker").value === true) ||
          (this.resource.workMode === "Offline" &&
            this.resourceForm.get("IsOfflineWorker").value === false)
        ) {
          console.log(
            "WARNING: IsOfflineWorker value doesn't match current workMode, but form isn't dirty!"
          );
          console.log("Forcing updateResource call to fix inconsistency");
          this.resourceForm.markAsDirty();
          this.updateResource();
        }
      }
    } else {
      console.log("Cannot save - resource is null", {
        resourceExists: !!this.resource
      });
    }

    console.log("====== SAVE RESOURCE WORKFLOW END ======");
  }

  // Efficiency changes don't reach the running solver until the solve state is
  // reloaded from the database, so warn before saving (same as zone/skill
  // changes). Any other resource edit saves straight away. (#1512)
  private confirmThenUpdateResource() {
    if (this.hasEfficiencyChanged()) {
      this.dialog
        .open(ConfirmEfficiencyChangesDialogComponent, { width: "500px" })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.updateResource();
          }
        });
    } else {
      this.updateResource();
    }
  }

  private hasEfficiencyChanged(): boolean {
    const base = this.efficiencyBaseline;
    if (!base) {
      return false;
    }
    const controlValue = (name: string) => this.resourceForm.get(name).value;
    const percentage = Number(controlValue("EfficiencyPercentage") ?? 100);
    const from = this.effDateKey(controlValue("EfficiencyEffectiveFrom"));
    const to = this.effDateKey(controlValue("EfficiencyEffectiveTo"));
    return (
      percentage !== base.percentage ||
      from !== this.effDateKey(base.effectiveFrom) ||
      to !== this.effDateKey(base.effectiveTo)
    );
  }

  // Normalise a datepicker value (moment, Date, ISO string or null) to a
  // YYYY-MM-DD key, so an edit-then-revert to the same day reads as unchanged
  // rather than relying on the control's dirty flag. (#1512)
  private effDateKey(value: unknown): string | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (typeof value === "string") {
      return value.slice(0, 10);
    }
    // moment is coerced to a Date via valueOf(); a real Date is used as-is.
    const date = value instanceof Date ? value : new Date(value as Date);
    if (isNaN(date.getTime())) {
      return null;
    }
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // Methods integrated from resource-property component
  updateResource() {
    // Save checkbox value before async operation to prevent it from being lost
    const isOfflineWorker = this.resourceForm.get("IsOfflineWorker").value;
    const newWorkMode = isOfflineWorker ? WorkMode.Offline : WorkMode.Online;

    // Fix: Store the team ID from the form immediately to prevent it from changing
    const selectedTeamId = this.resourceForm.get("Team").value;

    // Log initial state for debugging
    console.log("==== TEAM UPDATE WORKFLOW START ====");
    console.log("Initial form state: Team value =", selectedTeamId);
    console.log(
      "Initial form state: Team dirty =",
      this.resourceForm.get("Team").dirty
    );
    console.log("Current resource team:", this.resource?.team);

    this.resourceStore
      .loadSingle(this.resource.id)
      .then((value: ResourceModel) => {
        console.log("Loaded resource before update:", value);
        console.log("Loaded resource team:", value.team);

        // Set basic properties from form
        value.imageUrl = this.resourceForm.get("ResourceImageURL").value;
        value.cost = this.resourceForm.get("ResourceCost").value;
        value.name = this.resourceForm.get("ResourceName").value;

        // Efficiency settings — clamp client-side for friendly UX; server validates too.
        const rawEfficiency = this.resourceForm.get(
          "EfficiencyPercentage"
        ).value;
        const efficiency =
          typeof rawEfficiency === "number" && !Number.isNaN(rawEfficiency)
            ? Math.max(50, Math.min(150, Math.round(rawEfficiency)))
            : 100;
        value.efficiencyPercentage = efficiency;
        value.efficiencyEffectiveFrom =
          this.resourceForm.get("EfficiencyEffectiveFrom").value || null;
        value.efficiencyEffectiveTo =
          this.resourceForm.get("EfficiencyEffectiveTo").value || null;

        // Set work mode on the resource object
        value.workMode = newWorkMode;

        // Use the stored team ID instead of reading from form again
        console.log("Using stored teamId:", selectedTeamId);

        // Check if team has changed - more robust detection
        const hasTeamChanged =
          selectedTeamId &&
          (this.resourceForm.get("Team").dirty ||
            !value.team ||
            value.team.id !== selectedTeamId);

        console.log("Team change detection:", {
          hasTeamChanged,
          teamIdExists: !!selectedTeamId,
          teamControlDirty: this.resourceForm.get("Team").dirty,
          resourceHasTeam: !!value.team,
          teamMismatch: value.team ? value.team.id !== selectedTeamId : false
        });

        // Always update the base properties first
        console.log("Updating base resource properties...");
        this.resourceStore.update(value).subscribe((updatedResource) => {
          console.log("Base resource updated:", updatedResource);
          console.log("Updated resource team:", updatedResource.team);

          // If team has changed, update the team relation
          if (hasTeamChanged) {
            console.log("Team has changed - updating team relation");

            // Get the full team object from the store
            this.teamStore
              .loadSingle(selectedTeamId)
              .then((teamObject: Team) => {
                console.log("Loaded team object:", teamObject);

                // Use the already-updated resource directly - FIXED: Do not reload
                console.log("Using already updated resource directly (fix)");

                // Ensure workMode is preserved
                updatedResource.workMode = newWorkMode;

                // Then update team relation with the complete team object
                console.log("Binding team relation to resource...");
                updatedResource.bindRelation("team", teamObject).subscribe(
                  (bindResult) => {
                    console.log("Team relation binding result:", bindResult);

                    // Final update to ensure all properties are correctly saved
                    console.log("Performing final resource update...");
                    this.resourceStore.update(updatedResource).subscribe(
                      (finalResource) => {
                        console.log(
                          "Final resource update successful:",
                          finalResource
                        );
                        console.log("Final resource team:", finalResource.team);

                        // Force UI update with the selected team
                        console.log(
                          "Manually updating UI with new team:",
                          teamObject
                        );

                        // Update the resource object with the new team to fix the display issue
                        if (this.resource) {
                          this.resource.team = teamObject;
                          console.log(
                            "Updated resource.team in memory:",
                            this.resource.team
                          );
                        }

                        // Update form control with the selected team ID to keep UI in sync
                        this.resourceForm
                          .get("Team")
                          .setValue(selectedTeamId, { emitEvent: false });

                        this.resourceForm.markAsPristine();
                        console.log("==== TEAM UPDATE WORKFLOW COMPLETE ====");
                      },
                      (error) => {
                        console.error("Error in final resource update:", error);
                      }
                    );
                  },
                  (error) => {
                    console.error("Error binding team relation:", error);
                  }
                );
              });
          } else {
            console.log("No team change detected - skipping team update");

            // FIXED: Use the already updated resource directly instead of reloading
            console.log("Checking workMode directly on updatedResource (fix)");

            // Ensure workMode is still what we expect
            if (updatedResource.workMode !== newWorkMode) {
              console.log("WorkMode mismatch, fixing...");
              updatedResource.workMode = newWorkMode;

              // Do one more update to fix it
              this.resourceStore.update(updatedResource).subscribe(() => {
                // Just to keep the pattern consistent with the team change branch
                console.log("WorkMode update successful");

                // Ensure current team ID is properly set in the form
                if (this.resource && this.resource.team) {
                  console.log(
                    "Setting form team value to match current resource:",
                    this.resource.team.id
                  );
                  this.resourceForm
                    .get("Team")
                    .setValue(this.resource.team.id, { emitEvent: false });
                }

                this.resourceForm.markAsPristine();
                console.log("==== TEAM UPDATE WORKFLOW COMPLETE ====");
              });
            } else {
              this.resourceForm.markAsPristine();
              console.log("==== TEAM UPDATE WORKFLOW COMPLETE ====");
            }
          }
        });
      });
  }

  // Called when team selection changes
  onTeamSelectionChange(teamId: string) {
    console.log("==== TEAM SELECTION CHANGE ====");
    console.log("New teamId selected:", teamId);
    console.log("Previous teamId was:", this.resource?.team?.id);
    console.log("Form dirty state before change:", this.resourceForm.dirty);
    console.log(
      "Team control dirty state before change:",
      this.resourceForm.get("Team").dirty
    );

    if (teamId) {
      // Mark the entire form as dirty, not just the Team control
      this.resourceForm.get("Team").markAsDirty();
      this.resourceForm.markAsDirty();

      console.log("Form marked as dirty");
      console.log("Form dirty state after change:", this.resourceForm.dirty);
      console.log(
        "Team control dirty state after change:",
        this.resourceForm.get("Team").dirty
      );
    }
  }

  // Called when offline checkbox changes
  onOfflineWorkerChange(event: any) {
    this.resourceForm.get("IsOfflineWorker").markAsDirty();
    this.resourceForm.markAsDirty(); // Explicitly mark the whole form as dirty
  }

  // Load resource rules (excluded properties) for the current resource
  private loadResourceRules() {
    if (this.resource && this.resource.id) {
      console.log("Loading resource rules for resource:", this.resource);
      console.log(
        "ResourceRuleStore entities before load:",
        this.resourceRuleStore.entities
      );

      this.resourceRuleStore
        .findByResource(this.resource)
        .then((resourceRules) => {
          console.log("Resource rules loaded:", resourceRules);
          console.log(
            "ResourceRules count:",
            resourceRules?.resources?.length || 0
          );
          console.log(
            "ResourceRuleStore entities after load:",
            this.resourceRuleStore.entities
          );

          // Subscribe to see current entities
          this.resourceRuleStore.entities.subscribe((entities) => {
            console.log("Current entities in store:", entities);
            console.log("Current entities count:", entities?.length || 0);
          });
        })
        .catch((error) => {
          console.error("Error loading resource rules:", error);
          console.error("Error details:", error);
        });
    }
  }

  // Open dialog to manage excluded properties
  showExcludedPropertiesDialog() {
    this.dialog
      .open(ExcludedPropertiesDialogComponent, {
        width: "800px",
        data: {
          resource: this.resource,
          childEntityModel: this.childEntities.resourceRule
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh resource rules after dialog closes
        this.loadResourceRules();
        console.log("Excluded properties dialog closed");
      });
  }
}
